// Types et libellés des écrans des finances (lot L2-C, partie A). Les formes des réponses viennent
// des contrats de packages/contrats (recettes-*.ts) ; les réponses sans schéma de contrat (caisses,
// relevés, anomalies, remboursements) sont décrites ici d'après les services de apps/api/src/recettes.
import {
  BASES_CALCUL,
  CANAUX_PAIEMENT,
  ETATS_CREANCE,
  NATURES_RUBRIQUE,
  PERIODICITES_RUBRIQUE,
  TYPES_ANOMALIE,
  formaterFrancs,
  type BaseCalcul,
  type CanalPaiement,
  type CreanceReponse,
  type EtatCreance,
  type NatureRubrique,
  type PaiementReponse,
  type PeriodiciteRubrique,
  type QuittanceReponse,
  type RubriqueReponse,
  type TypeAnomalie,
} from '@cc/contrats';
import type { TonEtat } from '@cc/ui';

export type { CreanceReponse, PaiementReponse, QuittanceReponse, RubriqueReponse };
export type Tarif = RubriqueReponse['tarifs'][number];

/** Fiche d'une créance : la réponse de liste complétée des mouvements et des imputations. */
export interface CreanceDetail extends CreanceReponse {
  mouvements: { type: string; montant: number; motif: string | null; piece_document_id: string | null; donnees: Record<string, unknown> | null; auteur_appartenance_id: string | null; horodatage: string | null }[];
  imputations: { id: string; paiement_id: string; montant: number; remboursement_id: string | null; annulation: boolean; cree_le: string | null; reference_serveur: string; canal: string; paiement_etat: string; quittance_numero: string | null }[];
}

/** Fiche d'un paiement : notifications du prestataire et leur traitement, imputations, remboursements. */
export interface PaiementDetail extends PaiementReponse {
  notifications: { id: string; prestataire: string; reference_prestataire: string | null; statut_annonce: string; signature_valide: boolean; recue_le: string | null; traitement: string; detail: string | null }[];
  imputations: { creance_id: string; numero: string; montant: number; remboursement_id: string | null; annulation: boolean; cree_le: string | null }[];
  remboursements: { id: string; numero: string; montant: number; etat: string; motif: string }[];
}

export interface Remboursement {
  id: string;
  numero: string;
  paiement_id: string;
  reference_paiement: string | null;
  montant: number;
  motif: string;
  piece_document_id: string | null;
  etat: string;
  propose_par: string | null;
  propose_le: string | null;
  valide_par: string | null;
  valide_le: string | null;
  ordonne_par: string | null;
  ordonne_le: string | null;
  delegation_id: string | null;
  motif_rejet: string | null;
  detail: Record<string, unknown> | null;
}

export interface Caisse {
  id: string;
  libelle: string;
  lieu: string;
  regisseur_appartenance_id: string;
  regisseur: string;
  active: boolean;
  a_verifier: number;
}

export interface RecuCaisse {
  id: string;
  numero: string;
  caisse_id: string;
  creance_id: string;
  creance_numero: string | null;
  redevable_id: string;
  montant: number;
  emis_le: string;
  etat: string;
  cloture_id: string | null;
  paiement_id: string | null;
  motif_annulation: string | null;
}

export interface ClotureCaisse {
  id: string;
  caisse_id: string;
  date: string;
  nombre_recus: number;
  total_recus: number;
  montant_constate: number;
  ecart: number;
  bordereau_document_id: string | null;
  cloturee_le: string | null;
  verifiee_par: string | null;
  verifiee_le: string | null;
  quittances?: string[];
}

export interface SyntheseReleve {
  lignes: number;
  auto: number;
  manuel: number;
  ecarts: number;
  absents_du_releve: number;
  total_releve: number;
  total_rapproche: number;
  integral: boolean;
}

export interface ReleveResume extends SyntheseReleve {
  id: string;
  canal: string;
  prestataire: string | null;
  periode: { debut: string | null; fin: string | null };
  importe_le: string | null;
}

export interface LigneRapprochement {
  id: string;
  origine: string;
  rang: number;
  reference: string | null;
  montant: number | null;
  date: string | null;
  etat: string;
  paiement_id: string | null;
  paiement_reference: string | null;
  motif: string | null;
}

export interface ReleveDetail {
  id: string;
  canal: string;
  prestataire: string | null;
  periode: { debut: string | null; fin: string | null };
  fichier_document_id: string | null;
  importe_le: string | null;
  synthese: SyntheseReleve;
  lignes: LigneRapprochement[];
}

export interface Anomalie {
  id: string;
  type: string;
  libelle: string;
  objet: { type: string; id: string };
  detail: Record<string, unknown>;
  etat: string;
  responsable_appartenance_id: string | null;
  conclusion: string | null;
  conclue_le: string | null;
  cree_le: string;
}

export interface AnomalieDetail extends Anomalie {
  historique: { etat_avant: string | null; etat_apres: string; commentaire: string | null; auteur_appartenance_id: string | null; horodatage: string | null }[];
}

export interface Redevable {
  id: string;
  nom: string;
  type: string;
  usager_compte_id: string | null;
  identifiant_externe: string | null;
  reste: number;
}

/** Adaptateur de paiement configuré (GET /recettes/prestataires). */
export interface Prestataire {
  prestataire: string;
  canal: string;
  etat: 'non_configure' | 'configure_test' | 'configure_production' | string;
}

export const LIBELLES_ETAT_PRESTATAIRE: Record<string, string> = { non_configure: 'Non configuré', configure_test: 'Configuré (essai)', configure_production: 'Configuré (production)' };

/** Page du simulateur de paiement (GET /public/simulateur-paiement/:reference). */
export interface PageSimulateur {
  prestataire: string;
  mention: string;
  reference: string;
  montant: number;
  devise: string;
  beneficiaire: string;
  etat: string;
  actions: string[];
}

// --- Libellés -----------------------------------------------------------------------------

export const LIBELLES_ETAT_CREANCE: Record<string, string> = ETATS_CREANCE;
export const LIBELLES_NATURE: Record<string, string> = NATURES_RUBRIQUE;
export const LIBELLES_PERIODICITE: Record<string, string> = PERIODICITES_RUBRIQUE;
export const LIBELLES_BASE: Record<string, string> = BASES_CALCUL;
export const LIBELLES_TYPE_ANOMALIE: Record<string, string> = TYPES_ANOMALIE;

export const LIBELLES_STATUT_TARIF: Record<string, string> = { propose: 'Proposé', approuve: 'Approuvé', suspendu: 'Suspendu' };
export const LIBELLES_UNITE_TEMPS: Record<string, string> = { aucune: 'Montant unique', jour: 'Par jour', mois: 'Par mois', trimestre: 'Par trimestre', annee: 'Par an' };
export const LIBELLES_CATEGORIE: Record<string, string> = { principale: 'Rubrique principale', exoneration: 'Exonération liée', penalite: 'Pénalité liée' };

export const LIBELLES_ETAT_PAIEMENT: Record<string, string> = {
  initie: 'Initié',
  en_attente: 'En attente du prestataire',
  confirme: 'Confirmé',
  refuse: 'Refusé',
  expire: 'Expiré',
  annule: 'Annulé',
  rembourse: 'Remboursé',
};

export const LIBELLES_CANAL: Record<string, string> = { simulateur: 'Simulateur', mobile_money: 'Mobile money', banque: 'Banque', virement: 'Virement', terminal: 'Terminal de paiement', caisse: 'Caisse' };

export const LIBELLES_TRAITEMENT: Record<string, string> = {
  appliquee: 'Appliquée',
  ignoree_doublon: 'Ignorée (doublon)',
  tardive: 'Tardive (après expiration)',
  contradictoire: 'Contradictoire',
  invalide: 'Invalide',
};

export const LIBELLES_STATUT_ANNONCE: Record<string, string> = { confirme: 'Confirmé', refuse: 'Refusé', expire: 'Expiré' };

export const LIBELLES_ETAT_REMBOURSEMENT: Record<string, string> = { propose: 'Proposé', valide: 'Validé', ordonne: 'Ordonné', rejete: 'Rejeté' };
export const LIBELLES_ETAT_RECU: Record<string, string> = { emis: 'Émis', cloture: 'Clôturé', verifie: 'Vérifié', annule: 'Annulé' };
export const LIBELLES_ETAT_QUITTANCE: Record<string, string> = { valide: 'Valide', annulee: 'Annulée' };
export const LIBELLES_ETAT_ANOMALIE: Record<string, string> = { ouverte: 'Ouverte', en_instruction: 'En instruction', expliquee: 'Expliquée', confirmee: 'Confirmée' };
export const LIBELLES_ETAT_LIGNE: Record<string, string> = { auto: 'Rapprochée automatiquement', manuel: 'Rapprochée manuellement', ecart: 'Écart' };
export const LIBELLES_ORIGINE_LIGNE: Record<string, string> = { releve: 'Relevé', systeme: 'Système' };
export const LIBELLES_MOUVEMENT: Record<string, string> = { emission: 'Émission', remise: 'Remise', exoneration: 'Exonération', annulation: 'Annulation', relance: 'Relance' };
export const LIBELLES_NATURE_FAIT: Record<string, string> = {
  attribution_emplacement: 'Attribution d\'emplacement',
  autorisation_domaine_public: 'Autorisation du domaine public',
  demarche_payante: 'Démarche payante',
  reservation_equipement: 'Réservation d\'équipement',
  avis_imposition: 'Avis d\'imposition',
};

export const CODES_NATURE = Object.keys(NATURES_RUBRIQUE) as NatureRubrique[];
export const CODES_PERIODICITE = Object.keys(PERIODICITES_RUBRIQUE) as PeriodiciteRubrique[];
export const CODES_BASE = Object.keys(BASES_CALCUL) as BaseCalcul[];
export const CODES_ETAT_CREANCE = Object.keys(ETATS_CREANCE) as EtatCreance[];
export const CODES_CANAL = CANAUX_PAIEMENT as readonly CanalPaiement[];
export const CODES_TYPE_ANOMALIE = Object.keys(TYPES_ANOMALIE) as TypeAnomalie[];

// --- Formats et tons ----------------------------------------------------------------------

/** Montant en francs CFA (« 27 000 F ») ; un montant inconnu s'écrit « non renseigné ». */
export function francs(montant: number | null | undefined): string {
  return montant === null || montant === undefined || Number.isNaN(montant) ? 'non renseigné' : formaterFrancs(montant);
}

export function libelle(table: Record<string, string>, code: string | null | undefined): string {
  if (!code) return 'non renseigné';
  return table[code] ?? code;
}

export function tonCreance(c: Pick<CreanceReponse, 'etat' | 'echue'>): { ton: TonEtat; libelle: string } {
  if (c.etat === 'soldee') return { ton: 'ok', libelle: 'Soldée' };
  if (c.etat === 'annulee') return { ton: 'neutre', libelle: 'Annulée' };
  if (c.echue) return { ton: 'retard', libelle: 'Échue' };
  if (c.etat === 'partiellement_payee') return { ton: 'attente', libelle: 'Partiellement payée' };
  if (c.etat === 'en_contentieux') return { ton: 'retard', libelle: 'En contentieux' };
  return { ton: 'neutre', libelle: 'Émise' };
}

export function tonPaiement(p: Pick<PaiementReponse, 'etat' | 'en_anomalie'>): { ton: TonEtat; libelle: string } {
  const l = libelle(LIBELLES_ETAT_PAIEMENT, p.etat);
  if (p.en_anomalie) return { ton: 'retard', libelle: `${l}, en anomalie` };
  if (p.etat === 'confirme') return { ton: 'ok', libelle: l };
  if (p.etat === 'en_attente' || p.etat === 'initie') return { ton: 'attente', libelle: l };
  if (p.etat === 'refuse' || p.etat === 'expire') return { ton: 'retard', libelle: l };
  return { ton: 'neutre', libelle: l };
}

export function tonTarif(statut: string): TonEtat {
  if (statut === 'approuve') return 'ok';
  if (statut === 'propose') return 'signer';
  return 'neutre';
}

export function tonAnomalie(etat: string): TonEtat {
  if (etat === 'ouverte') return 'retard';
  if (etat === 'en_instruction') return 'attente';
  if (etat === 'confirmee') return 'retard';
  return 'ok';
}

export function tonRemboursement(etat: string): TonEtat {
  if (etat === 'ordonne') return 'ok';
  if (etat === 'rejete') return 'neutre';
  if (etat === 'valide') return 'signer';
  return 'attente';
}

export function tonRecu(etat: string): TonEtat {
  if (etat === 'verifie') return 'ok';
  if (etat === 'annule') return 'neutre';
  if (etat === 'cloture') return 'signer';
  return 'attente';
}

/** Part payée d'une créance, de 0 à 100, pour la réglette de la liste (S11). */
export function partPayee(c: Pick<CreanceReponse, 'montant_du' | 'montant_paye'>): number {
  if (c.montant_du <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((100 * c.montant_paye) / c.montant_du)));
}

/** Un tarif en texte : « 1 500 F par m² et par mois » ou « 2,5 % du montant déclaré », suffixé « (fictif) ». */
export function texteTarif(t: Pick<Tarif, 'montant' | 'pourcentage' | 'unite' | 'fictif'>): string {
  const base = t.montant !== null ? `${formaterFrancs(t.montant)} ${t.unite}` : `${(t.pourcentage ?? 0).toLocaleString('fr-FR')} % ${t.unite}`;
  return t.fictif ? `${base} (fictif)` : base;
}

/** Tarif approuvé en vigueur à une date (par défaut aujourd'hui). */
export function tarifEnVigueur(r: Pick<RubriqueReponse, 'tarifs'>, jour = new Date().toISOString().slice(0, 10)): Tarif | null {
  const candidats = r.tarifs.filter((t) => t.statut === 'approuve' && t.debut <= jour && (!t.fin || t.fin >= jour));
  return candidats.sort((a, b) => (a.debut < b.debut ? 1 : -1))[0] ?? null;
}

/** Une anomalie se rapporte à un objet des recettes : lien vers sa fiche quand l'écran existe. */
export function hrefObjetAnomalie(objet: { type: string; id: string }): string | null {
  const chemins: Record<string, string> = { creance: '/recettes/creances', paiement: '/recettes/paiements', caisse_cloture: '/recettes/caisses/clotures', releve: '/recettes/rapprochements', tarif: '/recettes/catalogue', remboursement: '/recettes/remboursements' };
  const base = chemins[objet.type];
  if (!base) return null;
  if (objet.type === 'tarif' || objet.type === 'caisse_cloture') return base;
  return `${base}/${objet.id}`;
}
